/**
 * forge-tryon entry point.
 *
 * Registers both <mirrably-eyewear> (new) and <forge-tryon> (deprecated) custom
 * elements and re-exports the public API so library consumers can import types
 * and helpers directly:
 *
 *   import { MirrablyEyewearElement, ForgeTryonConfig } from 'forge-tryon'
 */

import { ForgeTryonElement } from './core/widget.js';

// ── Registration ─────────────────────────────────────────────────────────────

/**
 * Register the custom elements.
 *
 * Safe to call multiple times -- subsequent calls are no-ops if the elements
 * are already defined.
 */
export function register(): void {
  // Primary: <forge-tryon> (backward compat -- still the main registration)
  if (!customElements.get('forge-tryon')) {
    customElements.define('forge-tryon', ForgeTryonElement);
  }

  // New name: <mirrably-eyewear> -- registered as a thin subclass that logs
  // the new name for analytics but otherwise behaves identically.
  if (!customElements.get('mirrably-eyewear')) {
    customElements.define('mirrably-eyewear', class MirrablyEyewearElement extends ForgeTryonElement {});
  }
}

// Auto-register on import (tree-shakeable: use `register()` export if you
// need manual control over timing)
register();

// ── Public API exports ────────────────────────────────────────────────────────

/** @deprecated Use MirrablyEyewearElement instead. */
export { ForgeTryonElement } from './core/widget.js';
/** Primary element class for the eyewear widget. */
export { ForgeTryonElement as MirrablyEyewearElement } from './core/widget.js';

export type { ForgeTryonConfig } from './core/config.js';
export type { ForgeTryonEvent, ForgeTryonEventName, ForgeTryonListener } from './core/events.js';

// Re-export shared types from @mirrably/* packages
export type { EyewearLandmarks, ITracker, Point3D } from '@mirrably/tracking';
export type { Product, Variant, IDataProvider, MeshMaterialOverride } from '@mirrably/core';
export { DataProviderError, RestDataProvider, CameraManager, CameraError, Analytics, createGA4Callback } from '@mirrably/core';
export type { RestProviderConfig, CameraState, AnalyticsCallback, AnalyticsEvent } from '@mirrably/core';

// Backward-compat type aliases
export type {
  EyewearLandmarks as FaceLandmarks,
  ITracker as IFaceTracker,
} from '@mirrably/tracking';
